import React, { ChangeEvent, InputHTMLAttributes, useCallback, useEffect, useState } from 'react';
import InputMask from 'react-text-mask';
import { dateMask, percentageMask } from './inputMasks';
import debounce from 'lodash.debounce';

export interface IMaskedFieldProps {
  mask?: (string | RegExp)[];
  showMask?: boolean;
  currency?: boolean;
}

export interface IProps extends InputHTMLAttributes<HTMLInputElement> {
  form: any;
  field: any;
  handleBlur?: any;
  transformValue?: (value: string) => string;
  CustomComponent?: any;
  showError?: boolean;
  debounceMs?: number;
}

const pattern = new RegExp(/[()\-,_ %£]+|(years)+/, 'g');

const Input: React.FC<IProps & IMaskedFieldProps> = (props: IProps & IMaskedFieldProps) => {
  const {
    mask = null,
    form,
    type,
    className,
    placeholder,
    field,
    showMask = false,
    disabled,
    handleBlur,
    readOnly,
    showError,
    transformValue = (value: string) => value,
    CustomComponent,
    debounceMs = 300,
    ...rest
  } = props;
  const inputProps = { type, className, placeholder, disabled, ...field, ...rest };
  const hasMask = !!mask;

  const [fieldValue, setFieldValue] = useState<any>();

  useEffect(() => {
    if (fieldValue !== field.value) {
      setFieldValue(field.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const triggerUpdate = useCallback(
    form && form.setFieldValue
      ? debounceMs > 0
        ? debounce(form.setFieldValue, debounceMs)
        : form.setFieldValue
      : () => {},
    [form],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const triggerRestOnChange = useCallback(
    rest && rest.onChange ? (debounceMs > 0 ? debounce(rest.onChange, debounceMs) : rest.onChange) : () => {},
    [rest],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const triggerOnChange = useCallback(
    field && field.onChange ? (debounceMs > 0 ? debounce(field.onChange, debounceMs) : field.onChange) : () => {},
    [field],
  );

  const handleInputMaskChange = (event: any) => {
    const rawValue = event.target.value;
    const isNegative = rawValue.match(/^-/g) !== null;
    const value = mask === dateMask ? rawValue : rawValue.replace(pattern, '');
    const transformedValue = transformValue(isNegative ? `-${value}` : value);
    setFieldValue(transformedValue);
    triggerOnChange({ ...event, target: { ...event.target, value: transformedValue } });
    triggerRestOnChange({ ...event, target: { ...event.target, value: transformedValue } });
    triggerUpdate(field.name, transformedValue);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFieldValue(value);
    triggerUpdate(field.name, value);
  };

  const onBlur = (e: any) => {
    if (handleBlur) {
      handleBlur(e);
    }

    if (hasMask && mask === percentageMask && !e.target.value.includes('%')) {
      e.target.value += '%';
    }
  };

  return hasMask ? (
    <InputMask
      {...inputProps}
      value={fieldValue}
      defaultValue={''}
      mask={mask}
      guide={showMask}
      onChange={handleInputMaskChange}
      onBlurCapture={onBlur}
      readOnly={readOnly}
      render={
        CustomComponent
          ? (ref, props) => (
              <CustomComponent state={showError ? 'error' : fieldValue ? 'filled' : undefined} ref={ref} {...props} />
            )
          : undefined
      }
    />
  ) : CustomComponent ? (
    <CustomComponent
      {...inputProps}
      value={fieldValue}
      onChange={handleChange}
      state={showError ? 'error' : fieldValue ? 'filled' : undefined}
      onBlurCapture={handleBlur}
      readOnly={readOnly}
    />
  ) : (
    <input {...inputProps} value={fieldValue} onChange={handleChange} onBlurCapture={handleBlur} readOnly={readOnly} />
  );
};

export default Input;
